import { useClientDataTable } from '../shared-compat/DataTable/hooks';
import { useQuery } from '@tanstack/react-query';
import { getLegacyPortalApi } from '../legacy-portal-api/legacy-api';
import React, { useMemo } from 'react';
import { DataTable } from '../shared-compat/DataTable/DataTable';
import { useModal } from '../hooks/useModal';
import { alertColumnsDef } from './AlertTableDefinition';
import '../menu-modal/MenuModal/portal-active-alert-context';
import DarkSelect from '../shared-compat/utils/DarkSelect/DarkSelect';
import styles from './AlertTable.module.scss';
import type { AlertRowColor } from '../menu-modal/MenuModal/portal-active-alert-context';

export function useAlertTable() {
  const legacyPortalApi = getLegacyPortalApi();
  const alertColumns = useMemo(() => alertColumnsDef(), []);

  const [alertTypeSelected, setAlertTypeSelected] = React.useState<string>('Asset');
  const getActiveAlertsQuery = useQuery({
    queryKey: ['active-alerts-table'],
    queryFn: async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const companyId = urlParams.get('c') || '';
      const siteId = urlParams.get('s') || '';
      const assetId = urlParams.get('a') || '';

      return await legacyPortalApi.getActiveAlerts('', companyId || '', siteId || '', assetId || '');
    },
    refetchInterval: 60000, // every 1 minute
    refetchOnWindowFocus: true,
    enabled: true
  });

  const filteredActiveAlertData = useMemo(() => {
    return alertTypeSelected === 'All' ? getActiveAlertsQuery.data : (getActiveAlertsQuery.data ?? []).filter((alert) => alert.Type === alertTypeSelected);
  }, [getActiveAlertsQuery.data, alertTypeSelected]);

  // Temporarily using the client table until the backend is implemented.
  const alertTable = useClientDataTable({
    enableGlobalSearchBar: true,
    initialPageSize: 200,
    data: filteredActiveAlertData ?? [],
    isFetching: getActiveAlertsQuery.isLoading,
    // @ts-ignore - suppressing this error because this will be converted to server table once the backend is implemented.
    columns: alertColumns,
    initialSorting: [
      { id: 'Severity', desc: false },
      { id: 'Start', desc: false }
    ]
  });

  const alertTableModal = useModal<unknown, unknown>();
  return { alertTable, alertTableModal, alertTypeSelected, setAlertTypeSelected, getActiveAlertsQuery };
}

export function AlertTable() {
  const { alertTable, alertTypeSelected, setAlertTypeSelected } = useAlertTable();

  const typeOptions = useMemo(() => {
    return [
      { value: 'All', label: 'All' },
      { value: 'Asset', label: 'Asset' },
      { value: 'System', label: 'System' }
    ];
  }, []);

  return (
    <div style={{ padding: '10px' }}>
      <h3 className='m-t-10 mb-3 '>
        <i className='active-alerts-icon fas fa-lg fa-fw m-r-10 fa-exclamation-triangle' />
        ACTIVE
      </h3>

      <div className={styles['active_alerts']}>
        <DataTable
          header={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '10px' }}>Filter by Type</label>
              <DarkSelect
                options={typeOptions}
                value={alertTypeSelected}
                onChange={(value) => {
                  setAlertTypeSelected(value);
                }}
              />
            </div>
          }
          negativeMarginsOnMobile={true}
          onRowClick={(row) => {
            window.initializeActiveAlertsMenu(row, 'Asset', row.Subject);
          }}
          table={alertTable}
          bordered={true}
          striped={false}
          rowClassName={(row) => getRowClassName(row.Color)}
        />
      </div>
    </div>
  );
}

function getRowClassName(color: AlertRowColor): string {
  switch (color) {
    case 'red':
      return styles['row-red'];
    case 'orange':
      return styles['row-orange'];
    case 'gray':
      return styles['row-gray'];
    case 'blue':
      return styles['row-blue'];
  }
}
